import React, { useState, useRef, FormEvent, useEffect, useCallback } from "react";

import { DeviceEnum } from "../../types/context-detection";
import { DeviceScrollEnum } from "../../types/suggestions";
import { IHandleKeyboardNavigation } from "../../types/keyboard-navigation";
import { ISuggestionResponse } from "../../types/suggestion-response";
import { SearchBoxProps } from "../../types/searchbox";

import { L10nContext, NavigationContext } from "../common/contexts";
import { PageViewLogDataId } from "../../util/pageViewLogDataId";
import { sendKrakenEvent } from "../../util/kraken";
import { subscribeToPriceMode, getCurrentPriceMode, PriceMode } from "../../util/priceModeObserver";
import { useKeyboardNavigation } from "../common/keyboard-navigation";
import { useL10n } from "../common/l10n";
import debounce from "../../util/debounce";
import defaultSuggestionResponse from "../../util/defaultSuggestionResponse";

import Modal from "../modal/modal";
import SearchIcon from "../../Assets/svg/Search_2021_icon";
import SuggestionColumns from "../suggestion-list/suggestion-columns";
import SuggestionList from "../suggestion-list/suggestion-list";
import useSuggestionsRenderView from "../suggestion-list/suggestions-render-view";

import "./searchbox-grid.scss";
import "../suggestion-list/suggestions-grid.scss";

const SearchBoxGrid = ({ detectedContext, ...props }: SearchBoxProps) => {
    const { isWebShop, isMobileShop, device, customerType } = detectedContext;
    const SEARCH_TERM_LENGTH_LIMIT = 50;
    const TOP_SEARCH_QUERIES_LIMIT = isWebShop ? 10 : 5;
    const isDesktop = device === DeviceEnum.Desktop;
    const inputTabNavAttribute = { focusable: true };
    const showColumns = useSuggestionsRenderView("grid", isMobileShop);

    const [focus, setFocus] = useState(false);
    const [input, setInput] = useState("");
    const [priceMode, setPriceMode] = useState<PriceMode>("gross");
    const [validSearchTerm, setValidSearchTerm] = useState("");
    const [suggestions, setSuggestions] = useState<ISuggestionResponse>(
        defaultSuggestionResponse()
    );
    const [hasSuggestions, setHasSuggestions] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const [showModal, setShowModal] = useState(false);

    const scopeSswRef = useRef<HTMLDivElement | null>(null);
    const searchBoxRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const focusRef = useRef(focus);
    const suggestionsRef = useRef(suggestions);
    const abortControllerRef = useRef<AbortController | null>(null);
    const keyboardNavColumnElementRef = useRef<IHandleKeyboardNavigation | null>(null);
    const keyboardNavListElementRef = useRef<IHandleKeyboardNavigation | null>(null);

    const pageViewLogDataId = PageViewLogDataId.get();

    const l10n = useL10n(props.l10n);

    useEffect(() => {
        if (searchBoxRef.current) {
            scopeSswRef.current = searchBoxRef.current.closest(
                ".scope-ssw"
            ) as HTMLDivElement | null;
        }
    }, []);

    useEffect(() => {
        focusRef.current = focus;
    }, [focus]);

    useEffect(() => {
        setPriceMode(getCurrentPriceMode());
        const unsubscribe = subscribeToPriceMode((priceMode) => setPriceMode(priceMode));
        return unsubscribe;
    }, []);

    useEffect(() => {
        suggestionsRef.current = suggestions;
    }, [suggestions]);

    useEffect(() => {
        const hasSuggestions =
            suggestions.articles.length > 0 ||
            suggestions.flexibleSets.length > 0 ||
            suggestions.contents.length > 0 ||
            suggestions.topSearchQueries.length > 0;
        setHasSuggestions(hasSuggestions);
    }, [suggestions]);

    useEffect(() => {
        const showSuggestions = hasSuggestions && focus && !!validSearchTerm;
        setShowSuggestions(showSuggestions);
    }, [hasSuggestions, focus, validSearchTerm]);

    const handleModalKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if ((event.key === "Escape" || event.key === "Esc") && showModal) {
                event.preventDefault();
                setShowModal(false);
                inputRef.current?.focus();
            }
        },
        [showModal]
    );

    const handleClick = useCallback((event: Event) => {
        const target = event.target as HTMLElement;

        if (!searchBoxRef.current?.contains(target)) handleBlur();
    }, []);

    useEffect(() => {
        document.addEventListener("mousedown", handleClick);

        return () => {
            document.removeEventListener("mousedown", handleClick);
        };
    }, [handleClick]);

    useEffect(() => {
        document.addEventListener("keydown", handleModalKeyDown);

        return () => {
            document.removeEventListener("keydown", handleModalKeyDown);
        };
    }, [handleModalKeyDown]);

    useEffect(() => {
        if (!scopeSswRef.current) return;

        if (showSuggestions) {
            scopeSswRef.current.classList.add("suggestions-visible");
        } else {
            scopeSswRef.current.classList.remove("suggestions-visible");
        }
    }, [showSuggestions]);

    const handleSubmit = useCallback(
        (e: FormEvent<HTMLFormElement> | KeyboardEvent) => {
            e.preventDefault();

            const trimmedInput = input.trim();
            if (!trimmedInput) {
                handleBlur();
                setShowModal(true);
                return;
            }

            window.location.href = `${props.searchTarget}?query=${encodeURIComponent(
                trimmedInput
            )}`;
        },
        [input, props.searchTarget]
    );

    const navigationApi = useKeyboardNavigation(
        showColumns
            ? () => keyboardNavColumnElementRef.current?.getNavigationElements() ?? []
            : () => keyboardNavListElementRef.current?.getNavigationElements() ?? []
    );

    const handleKeyPressedWhileFocused = useCallback(
        (event: KeyboardEvent) => {
            if (
                event.key === "Tab" ||
                (event.shiftKey && event.key === "Tab") ||
                ((event.key === "Escape" || event.key === "Esc") &&
                    (showColumns
                        ? keyboardNavColumnElementRef.current
                        : keyboardNavListElementRef.current) !== null)
            ) {
                handleBlur();
            } else if (event.key === "Enter" && !navigationApi.hasSelectedNavElement()) {
                handleSubmit(event);
            } else if (device === DeviceEnum.Desktop) {
                navigationApi.handleKeyDown(event);
            }
        },
        [device, showColumns, navigationApi, handleSubmit]
    );

    useEffect(() => {
        const input = inputRef.current;
        if (!input) return;

        if (focus) {
            input.addEventListener("keydown", handleKeyPressedWhileFocused);
        } else {
            input.removeEventListener("keydown", handleKeyPressedWhileFocused);
        }

        return () => {
            input.removeEventListener("keydown", handleKeyPressedWhileFocused);
        };
    }, [focus, handleKeyPressedWhileFocused]);

    useEffect(() => {
        const suggestionsVisible = hasSuggestions && showSuggestions;
        if (!suggestionsVisible) {
            navigationApi.resetNavigation();
        }
    }, [hasSuggestions, showSuggestions, navigationApi]);

    const resetSuggestionResponse = () => {
        setSuggestions(defaultSuggestionResponse());
    };

    const fetchSuggestions = async (input: string) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const headers: Record<string, string> = pageViewLogDataId
            ? { "X-PageView-ID": pageViewLogDataId }
            : {};

        try {
            const res = await fetch(
                `${props.apiUrl}suggestion/?searchTerm=${encodeURIComponent(
                    input
                )}&topSearchQueriesLimit=${TOP_SEARCH_QUERIES_LIMIT}`,
                { headers, signal: controller.signal }
            );

            if (!res.ok) throw new Error("Network response was not ok");

            const response = await res.json();

            if (
                response.articles.length ||
                response.flexibleSets.length ||
                response.contents.length ||
                response.topSearchQueries.length
            ) {
                const newSuggestions = response as ISuggestionResponse;

                if (JSON.stringify(newSuggestions) !== JSON.stringify(suggestionsRef.current)) {
                    sendKrakenEvent(input);
                }

                setSuggestions(newSuggestions);
            } else {
                resetSuggestionResponse();
            }
        } catch (error) {
            if (error instanceof Error && error.name !== "AbortError") {
                resetSuggestionResponse();
            }
        } finally {
            abortControllerRef.current = null;
        }
    };

    const handleBlur = () => {
        if (!focusRef.current) return;

        setFocus(false);
        inputRef.current?.blur();
    };

    const handleFocus = () => {
        if (focusRef.current) return;

        setFocus(true);
        if (isDesktop) scrollSearchboxToTopDesktop();
    };

    const activateFocusHelper = () => {
        handleFocus();
        inputRef.current?.focus();
    };

    const scrollSearchboxToTopDesktop = () => {
        const searchBox = searchBoxRef.current;
        if (!searchBox) return;

        // on small / little height devices e.g. tablets in landscape
        // scrolling the searchbox up to have more space for suggestions
        const offsetTop = searchBox.offsetTop;
        const heightBelowIncludingSearch: number = window.innerHeight - offsetTop;

        if (heightBelowIncludingSearch > 590) return;

        window.scroll({
            behavior: "smooth",
            top: offsetTop - 55
        });
    };

    const debouncedFetch = useRef<((input: string) => void) & { cancel: () => void }>(
        debounce((input: string) => {
            setValidSearchTerm(input);
            fetchSuggestions(input);
        }, 250)
    );

    const sanatizeInput = (input: string) => {
        const validInput = input.length > SEARCH_TERM_LENGTH_LIMIT ? input.slice(0, 50) : input;
        return validInput;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value ?? "";
        const sanatizedInput = sanatizeInput(value);
        setInput(sanatizedInput);

        const trimmedInput = sanatizedInput.trim();
        if (!trimmedInput) {
            debouncedFetch.current.cancel();
            setValidSearchTerm("");
            resetSuggestionResponse();
        } else {
            debouncedFetch.current(trimmedInput);
        }
    };

    const hideModal = () => {
        setShowModal(false);
        inputRef.current?.focus();
    };

    return (
        <L10nContext.Provider value={l10n}>
            <div
                className={
                    "searchbox-grid" +
                    ` ssw-current-pricemode-${priceMode}` +
                    ` ${focus ? "focus" : ""}` +
                    ` ${showSuggestions ? "show-suggestions" : ""}` +
                    ` ${props.tenant}` +
                    ` ${props.isEsApp ? "esApp" : ""}`
                }
                ref={searchBoxRef}
            >
                <form
                    className={"search-grid"}
                    onSubmit={handleSubmit}
                    onClick={activateFocusHelper}
                >
                    <button
                        type="submit"
                        className="search-grid-button"
                        aria-label={l10n.defaultTerm}
                    >
                        <SearchIcon />
                    </button>

                    <span className="transition-wrapper">
                        <span
                            className="input-sizer"
                            data-value={validSearchTerm || (focus ? "" : l10n.defaultTerm)}
                        >
                            <input
                                ref={inputRef}
                                value={input}
                                maxLength={50}
                                onChange={handleChange}
                                onFocus={handleFocus}
                                placeholder={focus ? "" : l10n.defaultTerm}
                                className="search-grid-input"
                                type="text"
                                name="query"
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="none"
                                spellCheck="false"
                                aria-label={l10n.defaultTerm}
                                size={1}
                                {...inputTabNavAttribute}
                            />
                        </span>
                    </span>
                </form>

                <hr className={"grid-line"} />

                <div id={`new-suggestions-${props.view}`} className={"new-suggestions"}>
                    {showSuggestions && (
                        <>
                            {showColumns ? (
                                <NavigationContext.Provider value={navigationApi}>
                                    <SuggestionColumns
                                        navigationRef={keyboardNavColumnElementRef}
                                        portal={props.portal}
                                        culture={props.culture}
                                        searchTarget={props.searchTarget}
                                        trackingEndpoint={props.trackingEndpoint}
                                        articles={suggestions.articles}
                                        flexibleSets={suggestions.flexibleSets}
                                        contents={suggestions.contents}
                                        featurePromotion={suggestions.featurePromotion}
                                        topSearchQueries={suggestions.topSearchQueries}
                                        priceMandantSettings={
                                            suggestions.priceMandantSettingsArticles
                                        }
                                        priceMandantSettingsV2={
                                            suggestions.priceMandantSettingsFlexibleSet
                                        }
                                        customerType={customerType}
                                        searchTerm={validSearchTerm}
                                        isMobileShop={isMobileShop}
                                        view={props.view}
                                        pageViewLogDataId={pageViewLogDataId}
                                        deviceScrollClass={DeviceScrollEnum.None}
                                        deviceType={device}
                                    />
                                </NavigationContext.Provider>
                            ) : (
                                <NavigationContext.Provider value={navigationApi}>
                                    <SuggestionList
                                        navigationRef={keyboardNavListElementRef}
                                        portal={props.portal}
                                        culture={props.culture}
                                        searchTarget={props.searchTarget}
                                        trackingEndpoint={props.trackingEndpoint}
                                        articles={suggestions.articles}
                                        flexibleSets={suggestions.flexibleSets}
                                        contents={suggestions.contents}
                                        featurePromotion={suggestions.featurePromotion}
                                        topSearchQueries={suggestions.topSearchQueries}
                                        priceMandantSettings={
                                            suggestions.priceMandantSettingsArticles
                                        }
                                        priceMandantSettingsV2={
                                            suggestions.priceMandantSettingsFlexibleSet
                                        }
                                        customerType={customerType}
                                        searchTerm={validSearchTerm}
                                        isMobileShop={isMobileShop}
                                        view={props.view}
                                        pageViewLogDataId={pageViewLogDataId}
                                        deviceType={device}
                                    />
                                </NavigationContext.Provider>
                            )}
                        </>
                    )}
                </div>

                {isWebShop && (
                    <Modal
                        open={showModal}
                        parentName={props.view}
                        tenant={props.tenant}
                        onClose={() => hideModal()}
                    />
                )}
            </div>
        </L10nContext.Provider>
    );
};

export default SearchBoxGrid;
